import React from 'react';

import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import BinIcon from 'icons/bin-filled.svg';
import RotateIcon from 'icons/rotate-right.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import tailwindConfig from 'stylesheets/tailwind.config';

const { colors } = tailwindConfig.theme;

export function AttachmentActions({
  attachmentHashId,
  attachmentSize,
  isProcessed,
  isRotating,
  onDeleteButtonClick,
  onRotate,
  onSelectSize,
}) {
  const { t: i18nTCommon } = useTranslation('common');

  const isResizing =
    useIsMutating({
      mutationKey: ['updateAttachment'],
      predicate: (mutation) => {
        const {
          state: {
            variables: { hashid, params },
          },
        } = mutation;

        return (
          hashid === attachmentHashId &&
          Object.keys(params || {}).includes('display_size')
        );
      },
    }) > 0;

  const isUpdating = isResizing || isRotating;
  const isRotateDisabled = isUpdating || !isProcessed;

  return (
    <div className='tw-flex tw-items-center tw-justify-between'>
      <div className='tw-flex tw-gap-2'>
        <div className='tw-inline-flex tw-w-24 tw-items-center tw-justify-between tw-rounded-xl tw-border-0 tw-bg-grey-200 tw-p-1'>
          {['S', 'M', 'L'].map((size, index) => (
            <button
              className={classNames(
                'tw-h-6.5 tw-w-6.5 tw-rounded-lg tw-border-0 tw-font-semibold tw-text-cyan-900',
                attachmentSize === index ? 'tw-bg-white' : 'tw-bg-transparent',
              )}
              disabled={isUpdating}
              key={size}
              onClick={() => onSelectSize(attachmentHashId, index)}
              type='button'
            >
              {size}
            </button>
          ))}
        </div>
      </div>
      <div className='tw-flex tw-items-center'>
        <button
          className='tw-mr-1 tw-h-6 tw-w-6 tw-border-0 tw-bg-transparent tw-font-semibold'
          disabled={isRotateDisabled}
          onClick={() => onRotate(attachmentHashId)}
          type='button'
        >
          <RotateIcon
            fill={isRotateDisabled ? colors.grey[200] : colors.black}
          />
          <span className='tw-sr-only'>
            {i18nTCommon('attachments.rotate')}
          </span>
        </button>
        <button
          className='tw-h-6 tw-w-6 tw-border-0 tw-bg-transparent tw-font-semibold tw-text-grey-900'
          disabled={isUpdating}
          onClick={() => onDeleteButtonClick(attachmentHashId)}
          type='button'
        >
          <BinIcon fill={isUpdating ? colors.grey[200] : colors.black} />
          <span className='tw-sr-only'>{i18nTCommon('shared.delete')}</span>
        </button>
      </div>
    </div>
  );
}

AttachmentActions.propTypes = {
  attachmentHashId: PropTypes.string.isRequired,
  attachmentSize: PropTypes.number.isRequired,
  isProcessed: PropTypes.bool.isRequired,
  isRotating: PropTypes.bool.isRequired,
  onDeleteButtonClick: PropTypes.func.isRequired,
  onRotate: PropTypes.func.isRequired,
  onSelectSize: PropTypes.func.isRequired,
};
